import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Tab,
  TabIndicator,
  TabList,
  Tabs,
  Image,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import ListIcon from "assets/img/dashboards/list-view.svg";
import BlocksIcon from "assets/img/dashboards/grid-view.svg";

type ViewMode = "list" | "grid";

interface ProjectsHeaderProps {
  tabIndex: number;
  onTabChange: (index: number) => void;
  activeMessagesCnt: number;
  completeMessagesCnt: number;
  draftsProjectsCnt: number;
  onStartNewOrder?: () => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  isMobile?: boolean;
}

interface ProjectTabProps {
  label: string;
  count?: number;
  isSelected: boolean;
}

const ProjectTab = ({ label, count, isSelected }: ProjectTabProps) => {
  return (
    <Tab
      px="9px"
      py="8px"
      lineHeight="20px"
      fontSize="14px"
      fontWeight="400"
      color="#0B0E10"
      borderRadius="8px"
      textShadow={isSelected ? "0 0 0.5px currentColor" : "none"}
      _selected={{
        fontWeight: "600",
        color: "#3A7BFF",
      }}
      // _hover={{ color: "#3A7BFF" }}
      _focus={{ boxShadow: "none" }}
      transition="all 0.2s"
      minW="fit-content"
    >
      <Flex align="center" gap="8px">
        {label}
        {count !== undefined && count > 0 && (
          <Badge
            bg="#FC5555"
            color="white"
            borderRadius="full"
            minW="20px"
            h="20px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="12px"
            fontWeight="600"
            px="6px"
          >
            {count}
          </Badge>
        )}
      </Flex>
    </Tab>
  );
};

export default function ProjectsHeader({
  tabIndex,
  onTabChange,
  activeMessagesCnt,
  completeMessagesCnt,
  draftsProjectsCnt,
  onStartNewOrder,
  viewMode = "list",
  onViewModeChange,
  isMobile,
}: ProjectsHeaderProps) {
  return (
    <Box mb="24px">
      {/* Tabs and Button Container */}
      <Box
        bg="#F5F7FA"
        borderRadius="12px"
        p={{ base: "16px", md: "15px 24px" }}
        border="1px solid"
        borderColor="#E5E7EB"
      >
        <Flex
          justify="space-between"
          align="center"
          flexDirection={{ base: "column", md: "row" }}
          gap={{ base: "16px", md: "0" }}
        >
          <Tabs
            variant="unstyled"
            index={tabIndex}
            onChange={onTabChange}
            width="auto"
            maxW="360px"
            sx={{
              "@media (max-width: 526px)": {
                width: "100%",
                maxW: "100%",
              },
            }}
          >
            <TabList 
              width="360px" 
              justifyContent="space-between" 
              borderBottom="none"
              sx={{
                "@media (max-width: 526px)": {
                  width: "100%",
                },
              }}
            >
              <ProjectTab
                label="Active"
                count={activeMessagesCnt}
                isSelected={tabIndex === 0}
              />
              <ProjectTab
                label="Completed"
                count={completeMessagesCnt}
                isSelected={tabIndex === 1}
              />
              <ProjectTab
                label="Draft"
                count={draftsProjectsCnt}
                isSelected={tabIndex === 2}
              />
            </TabList>
            <TabIndicator 
              mt="-2px" 
              height="1px" 
              bg="#2563EB" 
              borderRadius="1px"
            />
          </Tabs>

          {/* Right Side: View Mode Toggle + Start New Order Button */}
          <Flex align="center" gap="12px" width={{ base: "100%", md: "auto" }}>


            {/* Start New Order Button */}
            <Button
              leftIcon={<AddIcon w="12px" h="12px" />}
              bgColor="#3A7BFF"
              color="#FFFFFF"
              fontSize="14px"
              fontWeight="500"
              h="32px"
              borderRadius="25px"
              border="1px solid #3A7BFF"
              _hover={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              _loading={{ pointerEvents: "none" }}
              _active={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              _focus={{
                boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
              }}
              onClick={onStartNewOrder}
              flex={{ base: "1", md: "0 0 auto" }}
            >
              Start new order
            </Button>

            {onViewModeChange && !isMobile && (
              <Flex gap="4px" bg="white" borderRadius="8px" p="3px" border="1px solid" borderColor="#E5E7EB">
                <IconButton
                  aria-label="List view"
                  borderRadius="0"
                  icon={<Image src={ListIcon} width="20px" height="20px" alt="List view" />}
                  size="sm"
                  bg={viewMode === "list" ? "#F5F7FA" : "transparent"}
                  color={viewMode === "list" ? "white" : "#6B7280"}
                  _hover={{ bg: viewMode === "list" ? "#F5F7FA" : "#F3F4F6" }}
                  onClick={() => onViewModeChange("list")}
                />
                <IconButton
                  aria-label="Grid view"
                  borderRadius="0"
                  icon={<Image src={BlocksIcon} width="20px" height="20px" alt="Grid view" />}
                  size="sm"
                  bg={viewMode === "grid" ? "#F5F7FA" : "transparent"}
                  color={viewMode === "grid" ? "white" : "#6B7280"}
                  _hover={{ bg: viewMode === "grid" ? "#F5F7FA" : "#F3F4F6" }}
                  onClick={() => onViewModeChange("grid")}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}
